/**
 * Cross-platform Haptics Utility
 *
 * Provides safe wrappers for expo-haptics that work on web, iOS, and Android.
 * Web platform silently ignores haptic calls (browsers don't support haptics).
 *
 * Usage:
 * ```tsx
 * import { triggerSelection, triggerImpact, triggerNotification } from '@/lib/haptics';
 *
 * // On button press
 * triggerSelection();
 *
 * // On success/error
 * triggerNotification('success');
 *
 * // On heavy action
 * triggerImpact('medium');
 * ```
 */
import { Platform } from 'react-native';
import * as Haptics from 'expo-haptics';

/**
 * Trigger selection haptic feedback (light tap)
 * Used for: button presses, selections, toggles
 */
export async function triggerSelection(): Promise<void> {
  if (Platform.OS === 'web') return;

  try {
    await Haptics.selectionAsync();
  } catch (error) {
    // Silently fail - haptics are non-critical
    console.debug('[Haptics] Selection feedback failed:', error);
  }
}

/**
 * Trigger impact haptic feedback
 * Used for: drag-and-drop, collisions, animations
 *
 * @param style - Impact intensity: light, medium, or heavy
 */
export async function triggerImpact(
  style: Haptics.ImpactFeedbackStyle = Haptics.ImpactFeedbackStyle.Medium
): Promise<void> {
  if (Platform.OS === 'web') return;

  try {
    await Haptics.impactAsync(style);
  } catch (error) {
    console.warn('Haptic feedback unavailable:', error);
  }
}

/**
 * Trigger notification haptic feedback
 * Used for: success, warning, error notifications
 *
 * @param type - Notification type: success, warning, or error
 */
export async function triggerNotification(
  type: Haptics.NotificationFeedbackType = Haptics.NotificationFeedbackType.Success
): Promise<void> {
  if (Platform.OS === 'web') return;

  try {
    await Haptics.notificationAsync(type);
  } catch (error) {
    console.debug('[Haptics] Notification feedback failed:', error);
  }
}
